import type React from 'react';
import { useState } from 'react';

import Container from '@mui/material/Container';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import CloseIcon from 'assets/icons/icn _ 24px  _ close_empty.svg?react';
import MoreOptions from 'assets/icons/icn _ 24px _ more_options.svg?react';
import TMXAlert from 'components/alert/TMXAlert';
import { TransitionUp } from 'components/animations/Transition';
import StandardButton from 'components/buttons/StandardButton';
import AvoidanceGroupsDialog from 'components/dialogs/avoidance/AvoidanceGroupsDialog';
import AvoidanceRulesDialog from 'components/dialogs/avoidance/AvoidanceRulesDialog';
import ViewAvoidanceGroupDialog from 'components/dialogs/avoidance/ViewAvoidanceGroupDialog';
import DeleteAvoidanceDialog from 'components/dialogs/deleteAvoidanceDialog/DeleteAvoidanceDialog';
import AvoidanceRuleDeleteIcon from 'components/lists/AvoidanceRuleDeleteIcon';
import AvoidanceRuleOrGroupItem from 'components/lists/AvoidanceRuleOrGroupItem';
import AvoidanceRulesList, { type AvoidanceListItemInterface } from 'components/lists/AvoidanceRulesList';
import { useStyles } from 'components/overlays/avoidance/styles';
import TMXOverlay from 'components/overlays/TMXOverlay';
import TMXPopover from 'components/popovers/TMXPopover';
import { BodyLarge, BodyXLarge } from 'components/typography/Typography';
import { tournamentEngineMappedConstants } from 'constants/avoidance';
import { type AddAvoidanceContainerProps } from 'containers/eventsSplitDraws/avoidance/CreateAvoidanceContainer';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import {
  type GroupParticipantInterface,
  type PlainAvoidanceGroupInterface,
  type PlainAvoidanceRuleInterface,
} from 'typedefs/avoidance';
import { isDoublesOrTeamChecked } from 'utils/avoidanceUtils';

interface AddAvoidanceOverlayProps extends AddAvoidanceContainerProps {
  currentAvoidance: any;
  avoidanceFromStorage: any; // TODO: add avoidance type
  isDoubles: boolean;
  isTeam: boolean;
  currentRules: PlainAvoidanceRuleInterface[];
  groupParticipants: GroupParticipantInterface[];
  setRules(rules: PlainAvoidanceRuleInterface[]): void;
  createAvoidanceGroup(
    groupName: string,
    individualParticipantIds: string[],
    selectedGroup: PlainAvoidanceGroupInterface,
  ): void;
  deleteAvoidance(): void;
  deleteAvoidanceGroup(participantId: string): void;
  onOpen(): void;
  saveAvoidance(): void;
  setAvoidance(avoidanceRules): void;
}

const CreateAvoidanceOverlay: React.FC<React.PropsWithChildren<AddAvoidanceOverlayProps>> = ({
  currentAvoidance,
  avoidanceFromStorage,
  existingGroups,
  currentRules,
  groupParticipants,
  isOpen,
  isDoubles,
  isTeam,
  readOnly,
  onClose,
  createAvoidanceGroup,
  deleteAvoidance,
  deleteAvoidanceGroup,
  onOpen,
  saveAvoidance,
  setAvoidance,
  setRules,
}) => {
  const { t } = useTranslation();

  const createAvoidanceInProgress = useProgress('createAvoidance');
  const deleteAvoidanceInProgress = useProgress('deleteAvoidance');
  const openOrInProgress = createAvoidanceInProgress || deleteAvoidanceInProgress ? true : isOpen;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [rulesOpen, setRulesOpen] = useState<boolean>(false);
  const [groupsOpen, setGroupsOpen] = useState<boolean>(false);
  const [groupsPopoverAnchorEl, setGroupsPopoverAnchorEl] = useState<SVGSVGElement | null>(null);
  const [viewGroupsOpen, setViewGroupsOpen] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>('');
  const mappedToUnchecked = groupParticipants?.map((participant) => ({
    ...participant,
    checked: false,
  }));
  const currentAvoidanceGroups = existingGroups?.map<PlainAvoidanceGroupInterface>((group) => {
    const filteredParticipants = group?.individualParticipantIds?.filter((id) =>
      groupParticipants.find((p) => (isDoubles || isTeam ? p.individualParticipantIds?.includes(id) : p.id === id)),
    );
    const groupCheckedParticipants = groupParticipants?.filter((p) => isDoublesOrTeamChecked(p, filteredParticipants));
    return {
      id: group?.participantId,
      name: group?.participantName,
      participantIds: filteredParticipants || [],
      description: `${isDoubles || isTeam ? groupCheckedParticipants.length : filteredParticipants?.length} ${t(
        'members',
      )}`,
    };
  });
  const currentPolicyAttributes = currentAvoidance?.policyAttributes;
  const [participants, setParticipants] = useState<GroupParticipantInterface[]>(groupParticipants);
  const [selectedGroup, setSelectedGroup] = useState<PlainAvoidanceGroupInterface>();
  const selectedGroupParticipants = participants.filter(
    (participant) =>
      selectedGroup?.participantIds?.includes(participant.id) ||
      selectedGroup?.participantIds?.some((participantId) =>
        participant?.individualParticipantIds?.includes(participantId),
      ),
  );
  const title = avoidanceFromStorage ? t('editAvoidances') : t('addAvoidances');
  const ifReadOnlyTitle = readOnly ? t('viewAvoidances') : title;
  const { classes } = useStyles();
  const avoidanceGroupsListItems: AvoidanceListItemInterface[] = currentAvoidanceGroups?.map((group) => ({
    key: group.id,
    element: (
      <AvoidanceRuleOrGroupItem
        description={group.description}
        icon={<MoreOptions className={classes.popoverItem} onClick={(event) => handleClickMoreOptions(event, group)} />}
        name={group.name || group.participantName}
        readOnly={readOnly}
        type="GROUP"
        onClickViewGroup={() => handleOnOpenViewGroups(group)}
      />
    ),
  }));
  const avoidanceRulesListItems = currentRules
    .filter((rule) => rule.checked)
    .map<AvoidanceListItemInterface>((rule) => ({
      key: rule.index.toString(),
      element: (
        <AvoidanceRuleOrGroupItem
          icon={<AvoidanceRuleDeleteIcon rule={rule} onClick={removeAvoidanceRule} />}
          name={rule.name}
          readOnly={readOnly}
          type="RULE"
        />
      ),
    }));

  const chosenRule = currentRules?.some((rule) => rule.checked);

  const disabledSave = !chosenRule && (!avoidanceGroupsListItems || avoidanceGroupsListItems?.length === 0);

  const handleRulesClose = () => setRulesOpen(false);
  const handleRulesOpen = () => setRulesOpen(true);
  const handleGroupsClose = () => {
    setSelectedGroup(null);
    setGroupName('');
    setGroupsPopoverAnchorEl(null);
    setGroupsOpen(false);
  };
  const handleGroupsOpen = () => {
    if (selectedGroup) {
      const mappedToGroup = participants
        ?.map((participant) => {
          return {
            ...participant,
            checked:
              isTeam || isDoubles
                ? isDoublesOrTeamChecked(participant, selectedGroup.participantIds)
                : selectedGroup.participantIds.includes(participant.id),
          };
        })
        ?.filter((p) => groupParticipants?.find((gp) => gp.id === p.id));
      setParticipants(mappedToGroup);
    } else {
      setParticipants(mappedToUnchecked);
    }
    setGroupsOpen(true);
  };
  const handleClickMoreOptions = (event, group) => {
    setGroupsPopoverAnchorEl(event.currentTarget);
    setSelectedGroup(group);
    setGroupName(group.name || group.participantName);
  };
  const handleCloseMoreOptions = () => {
    setGroupsPopoverAnchorEl(null);
    setSelectedGroup(null);
    setGroupName('');
  };
  const handleSaveAvoidance = () => {
    saveAvoidance();
    onClose();
    setGroupName('');
    setSelectedGroup(null);
  };
  const handleOnChangeGroupName = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setGroupName(event.target.value);
  };
  const handleClearParticipants = () => {
    setSelectedGroup({
      ...selectedGroup,
      participantIds: [],
    });
    setParticipants(mappedToUnchecked);
  };
  const handleDeleteAvoidanceGroup = () => {
    setGroupsPopoverAnchorEl(null);
    deleteAvoidanceGroup(selectedGroup.id);
    setGroupName('');
    handleClearParticipants();
    setSelectedGroup(null);
  };
  const handleDeleteAvoidance = () => {
    deleteAvoidance();
    onClose();
  };
  const handleOnCloseViewGroups = () => {
    setViewGroupsOpen(false);
  };
  function handleOnOpenViewGroups(group) {
    setViewGroupsOpen(true);
    setSelectedGroup(group);
  }
  function removeAvoidanceRule(rule: PlainAvoidanceRuleInterface) {
    const newAvoidanceRules = currentRules.map((avoidanceRule) => ({
      ...avoidanceRule,
      checked: avoidanceRule.index === rule.index ? !avoidanceRule.checked : avoidanceRule.checked,
    }));
    // map checked rules to the tournament compliant rules
    const tournamentEngineCompliantRules = newAvoidanceRules
      .filter((rule) => rule.checked)
      .flatMap((rule) => tournamentEngineMappedConstants[rule.name]);

    const ruleKeys = currentPolicyAttributes.reduce((acc, obj) => {
      const key = `${obj.key}_${obj.significantCharacters}_${obj.directive}`;
      acc[key] = [...(acc[key] || []), obj];
      return acc;
    }, {});

    const newPolicyAttributes = tournamentEngineCompliantRules.reduce((acc, obj) => {
      const key = `${obj.key}_${obj.significantCharacters}_${obj.directive}`;
      acc.push(...(ruleKeys[key] || []));
      return acc;
    }, []);

    setRules(newAvoidanceRules);
    setAvoidance({
      ...currentAvoidance,
      policyAttributes: newPolicyAttributes,
    });
  }

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <TMXOverlay
        fullScreen
        open={openOrInProgress}
        scroll="paper"
        TransitionComponent={TransitionUp}
        TransitionProps={{ onEnter: onOpen }}
        onClose={onClose}
      >
        <DialogTitle className={classes.dialogTitleComponent}>
          <Grid container className={classes.titleContainer} direction="row" justifyContent="space-between">
            <Grid item />
            <Grid item>
              <Typography className={classes.titleTypography} variant="h1">
                {ifReadOnlyTitle}
              </Typography>
            </Grid>
            <Grid item>
              <CloseIcon className={classes.closeIcon} onClick={onClose} />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent className={classes.dialogContentComponent}>
          <Container className={classes.contentContainer} maxWidth="lg">
            <Grid container direction="column">
              <Grid item className={classes.contentDescriptionTypography}>
                {readOnly ? (
                  <TMXAlert icon={true}>{t('createAvoidanceOverlay.contentAlert')}</TMXAlert>
                ) : (
                  <BodyLarge>{t('createAvoidanceOverlay.contentDescBodyLarge')}</BodyLarge>
                )}
              </Grid>
              <Grid item>
                <BodyXLarge className={classes.contentText} variant="h1">
                  {t('rules')}
                </BodyXLarge>
              </Grid>
              <Grid item>
                <BodyLarge>{t('createAvoidanceOverlay.contentAvoidOne')}</BodyLarge>
              </Grid>
              {chosenRule && <AvoidanceRulesList listItems={avoidanceRulesListItems} />}
              {!readOnly && (
                <Grid item className={classes.chooseRulesButtonWrapper}>
                  <StandardButton className={classes.chooseRulesButton} variant="secondary" onClick={handleRulesOpen}>
                    {t('chooseRules')}
                  </StandardButton>
                </Grid>
              )}
            </Grid>
            <Divider className={classes.divider} />
            <Grid item>
              <BodyXLarge className={classes.contentText} variant="h1">
                {t('playerGrs')}
              </BodyXLarge>
            </Grid>
            <Grid item>
              <BodyLarge>{t('createAvoidanceOverlay.contentAvoidTwo')}</BodyLarge>
            </Grid>
            {avoidanceGroupsListItems?.length > 0 && <AvoidanceRulesList listItems={avoidanceGroupsListItems} />}
            {!readOnly && (
              <Grid item className={classes.chooseRulesButtonWrapper}>
                <StandardButton className={classes.chooseRulesButton} variant="secondary" onClick={handleGroupsOpen}>
                  {t('addGroup')}
                </StandardButton>
              </Grid>
            )}
            {disabledSave ? (
              <Grid item className={classes.errorWrapper}>
                <Typography className={classes.error} color="error">
                  {t('createAvoidanceOverlay.error')}
                </Typography>
              </Grid>
            ) : null}
          </Container>
        </DialogContent>
        <Container className={classes.actionsContainer} maxWidth="lg">
          <Grid container direction="row" justifyContent="space-between">
            <Grid item>
              {!readOnly && (
                <StandardButton
                  disabled={!avoidanceFromStorage}
                  loading={deleteAvoidanceInProgress}
                  variant="warning"
                  onClick={handleOpenDeleteDialog}
                >
                  {t('delete')}
                </StandardButton>
              )}
            </Grid>
            <Grid item>
              <Grid container direction="row" justifyContent="flex-end" spacing={2}>
                <Grid item>
                  <StandardButton className={classes.cancelActionButton} onClick={onClose}>
                    {readOnly ? 'Close' : 'Cancel'}
                  </StandardButton>
                </Grid>
                <Grid item>
                  {!readOnly && (
                    <StandardButton
                      disabled={disabledSave}
                      loading={createAvoidanceInProgress}
                      variant="primary"
                      onClick={handleSaveAvoidance}
                    >
                      {t('saveAvoidances')}
                    </StandardButton>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </TMXOverlay>
      <AvoidanceRulesDialog
        avoidanceRules={currentRules}
        isOpen={rulesOpen}
        setAvoidance={setAvoidance}
        setAvoidanceRules={setRules}
        setChosenAvoidanceRules={setRules}
        onClose={handleRulesClose}
      />
      <AvoidanceGroupsDialog
        changeGroupName={handleOnChangeGroupName}
        clearParticipants={handleClearParticipants}
        createAvoidanceGroup={createAvoidanceGroup}
        groupName={groupName}
        isDoubles={isDoubles}
        isOpen={groupsOpen}
        participants={participants}
        selectedGroup={selectedGroup}
        setGroupName={setGroupName}
        setParticipants={setParticipants}
        setSelectedGroup={setSelectedGroup}
        onClose={handleGroupsClose}
      />
      {readOnly && (
        <ViewAvoidanceGroupDialog
          groupName={selectedGroup?.name || selectedGroup?.participantName || ''}
          groupParticipants={selectedGroupParticipants}
          isOpen={viewGroupsOpen}
          onClose={handleOnCloseViewGroups}
        />
      )}
      <TMXPopover
        anchorEl={groupsPopoverAnchorEl}
        id="group-popover"
        open={!!groupsPopoverAnchorEl}
        onClose={handleCloseMoreOptions}
      >
        <List>
          <ListItem className={classes.popoverItem} onClick={handleGroupsOpen}>
            <Typography>{t('editViewGroup')}</Typography>
          </ListItem>
          <ListItem className={classes.popoverItem} onClick={handleDeleteAvoidanceGroup}>
            <Typography className={classes.deleteGroupTypography}>{t('deleteGroup')}</Typography>
          </ListItem>
        </List>
      </TMXPopover>
      <DeleteAvoidanceDialog
        loading={deleteAvoidanceInProgress}
        open={deleteAvoidanceInProgress ? true : deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDeleteAvoidance}
      />
    </>
  );
};

export default CreateAvoidanceOverlay;
